import React, { useEffect, useRef, useState } from "react";
import { useNotifications } from "../../hooks/useNotifications";
import { useI18n } from "../../i18n";

const timeAgo = (
  iso: string,
  t: (key: string, vars?: Record<string, string | number>) => string,
  locale: string
): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("common.justNow");
  if (mins < 60) return t("notifications.timeAgo.minutes", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("notifications.timeAgo.hours", { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return t("notifications.timeAgo.days", { count: days });
  return new Date(iso).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
  });
};

// Deal-specific notification bell for the Deals page header.
// Shows only deal-related notifications (e.g. reservation extended / expiring).
const DealsNotificationBell: React.FC = () => {
  const { items, unreadCount, markAllRead } = useNotifications();
  const { t, code } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Only deal-related notifications belong on this bell.
  const dealItems = items.filter((n) => n.type?.startsWith("deal"));
  const dealUnread = dealItems.filter((n) => !n.read).length;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next && dealUnread > 0) markAllRead();
      return next;
    });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label={t("notifications.deals.title")}
        onClick={toggle}
        className="relative text-gray-400 hover:text-gray-600"
      >
        <i className="fa-solid fa-bell fa-lg"></i>
        {dealUnread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {dealUnread > 9 ? t("common.countOverflow", { count: 9 }) : dealUnread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 z-50 w-80 rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <span className="text-sm font-semibold text-gray-900">
              {t("notifications.deals.title")}
            </span>
            {dealUnread > 0 && (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                {t("notifications.deals.newCount", { count: dealUnread })}
              </span>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {dealItems.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                {t("notifications.deals.empty")}
              </div>
            ) : (
              dealItems.map((n) => (
                <div
                  key={n.uuid}
                  className={`flex items-start gap-3 border-b border-gray-50 px-4 py-3 ${
                    n.read ? "" : "bg-red-50/40"
                  }`}
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-600">
                    <i className="fa-solid fa-calendar-check"></i>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-gray-900">
                        {n.title}
                      </span>
                      {!n.read && (
                        <span className="shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                          {t("notifications.deals.badgeNew")}
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      {n.message}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-400">
                      {timeAgo(n.createdAt, t, code)}
                    </span>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealsNotificationBell;
